import React, { useState } from 'react'
import {
  Box,
  Chip,
  IconButton,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import UndoIcon from '@mui/icons-material/Undo'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import type { OptionMappingWithProvenance } from '@app/src/types/categoryPlan'

const CREATE_NEW_CATEGORY = '__create_new__'

type OptionMappingRowProps = {
  option: OptionMappingWithProvenance
  categoryKeys: { key: string; label: string }[]
  currentCategoryKey: string
  onRename: (rawValue: string, newName: string) => void
  onMove: (rawValue: string, targetCategoryKey: string) => void
  onRemove: (rawValue: string) => void
  onRestore: (rawValue: string) => void
}

const OptionMappingRow: React.FC<OptionMappingRowProps> = ({
  option,
  categoryKeys,
  currentCategoryKey,
  onRename,
  onMove,
  onRemove,
  onRestore,
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(option.suggestedOptionName ?? option.rawValue)
  const isNew = option.existingOptionId == null
  const displayName = option.suggestedOptionName ?? option.rawValue
  const moveTargets = categoryKeys.filter(c => c.key !== currentCategoryKey)

  return (
    <TableRow sx={{ opacity: option.isRemoved ? 0.4 : 1, textDecoration: option.isRemoved ? 'line-through' : 'none' }}>
      <TableCell>
        <Typography variant="body2">{option.rawValue}</Typography>
      </TableCell>
      <TableCell sx={{ px: 1 }}>
        <ArrowForwardIcon fontSize="small" color="action" />
      </TableCell>
      <TableCell>
        {isNew && isEditing ? (
          <TextField
            size="small"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={() => {
              setIsEditing(false)
              if (editValue.trim() !== displayName) {
                onRename(option.rawValue, editValue.trim())
              }
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
            }}
            autoFocus
            sx={{ minWidth: 120 }}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="body2"
              onClick={isNew && !option.isRemoved ? () => setIsEditing(true) : undefined}
              sx={isNew ? { cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : {}}
            >
              {displayName}
            </Typography>
            <Chip
              label={isNew ? 'new' : 'existing'}
              size="small"
              variant="outlined"
              color={isNew ? 'info' : 'default'}
              sx={{ ml: 0.5 }}
            />
          </Box>
        )}
      </TableCell>
      <TableCell>
        {option.sourceColumns.length > 1 && (
          <Typography variant="body2" color="text.secondary">
            from {option.sourceColumns.join(', ')}
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        {option.isRemoved ? (
          <Tooltip title="Restore">
            <IconButton size="small" onClick={() => onRestore(option.rawValue)}>
              <UndoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {moveTargets.length > 0 && (
              <Select
                size="small"
                value=""
                displayEmpty
                onChange={e => {
                  const value = e.target.value as string
                  if (value === CREATE_NEW_CATEGORY) {
                    const name = window.prompt('New category name:')
                    if (name?.trim()) onMove(option.rawValue, `new:${name.trim()}`)
                  } else {
                    onMove(option.rawValue, value)
                  }
                }}
                renderValue={() => (
                  <Tooltip title="Move to category">
                    <DriveFileMoveOutlinedIcon fontSize="small" />
                  </Tooltip>
                )}
                sx={{ minWidth: 40, '& .MuiSelect-select': { py: 0.5, px: 1 } }}
              >
                {moveTargets.map(t => (
                  <MenuItem key={t.key} value={t.key}>
                    {t.label}
                  </MenuItem>
                ))}
                <MenuItem value={CREATE_NEW_CATEGORY} sx={{ fontStyle: 'italic' }}>
                  Create new category...
                </MenuItem>
              </Select>
            )}
            <Tooltip title="Remove">
              <IconButton size="small" onClick={() => onRemove(option.rawValue)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </TableCell>
    </TableRow>
  )
}

export default OptionMappingRow
